import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ProductsService } from '../../../../api';
import { useDebounce } from '../../../../hooks/useDebounce';

export interface Product {
  id: number;
  name: string;
  code: string;
}

interface UseAsyncProductSearchProps {
  searchTerm: string;
  selectedProductIds?: number[];
  currentProductId?: number;
  onLoadMore?: () => void;
  enabled?: boolean;
}

export const useAsyncProductSearch = ({
  searchTerm,
  selectedProductIds = [],
  currentProductId,
  onLoadMore,
  enabled = true,
}: UseAsyncProductSearchProps) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const loadingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const BROWSE_ITEMS_PER_PAGE = 20;
  const SEARCH_ITEMS_PER_PAGE = 50;
  const selectedProductIdsKey = useMemo(
    () => [...selectedProductIds].sort((a, b) => a - b).join(','),
    [selectedProductIds],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedProductIdsSet = useMemo(() => new Set(selectedProductIds), [selectedProductIdsKey]);

  // Fetch products function
  const fetchProducts = useCallback(
    async (pageNum: number, search: string, append: boolean = false) => {
      if (!enabled) return;
      if (loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);

      // Cancel previous request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      const hasSearch = search.trim() !== '';
      const itemsPerPage = hasSearch ? SEARCH_ITEMS_PER_PAGE : BROWSE_ITEMS_PER_PAGE;

      try {
        const response = await ProductsService.productsControllerFindAll(
          pageNum,
          itemsPerPage,
          undefined, // searchName
          undefined, // searchCode
          search ?? undefined, // searchTerm - searches both name and code
          undefined, // categoryId
          'ACTIVE', // status - only active products
          undefined, // supplierId
          undefined, // minWeight
          undefined, // maxWeight
          undefined, // searchTag
          undefined, // materialId
          undefined, // diameterSize
          undefined, // color
          undefined, // workshopCode
        );

        if (response && response.data && Array.isArray(response.data)) {
          // Ignore stale responses if a newer request was fired.
          if (requestId !== requestIdRef.current) {
            return;
          }

          const newProducts: Product[] = response.data.map((item: any) => ({
            id: item.id,
            name: item.name,
            code: item.code,
          }));

          // Filter out already selected products (except current one)
          const filteredProducts = newProducts.filter(
            (product) => !selectedProductIdsSet.has(product.id) || product.id === currentProductId,
          );

          if (append) {
            setProducts((prev) => {
              const merged = [...prev, ...filteredProducts];
              const uniqueById = new Map<number, Product>();
              merged.forEach((product) => uniqueById.set(product.id, product));
              return Array.from(uniqueById.values());
            });
          } else {
            setProducts(filteredProducts);
          }

          // Check if there are more pages
          const total = (response as any).meta?.total ?? response.total ?? 0;
          setTotalItems(total);
          const totalPages = Math.ceil(total / itemsPerPage);
          setHasMore(pageNum < totalPages);
        } else {
          if (!append) {
            setProducts([]);
          }
          setHasMore(false);
        }
      } catch (error: any) {
        // Ignore abort errors
        if (error.name !== 'AbortError') {
          console.error('Error fetching products:', error);
          if (!append) {
            setProducts([]);
          }
        }
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [selectedProductIdsSet, currentProductId, enabled],
  );

  // Reset and fetch when search term changes
  useEffect(() => {
    if (!enabled) {
      setProducts([]);
      setHasMore(false);
      setLoading(false);
      loadingRef.current = false;
      return;
    }
    setPage(1);
    setProducts([]);
    setHasMore(true);
    fetchProducts(1, debouncedSearchTerm, false);
  }, [debouncedSearchTerm, fetchProducts, enabled]);

  // Load more products (for infinite scroll)
  const loadMore = useCallback(() => {
    if (!enabled) return;
    if (!loading && hasMore && !loadingRef.current) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchProducts(nextPage, debouncedSearchTerm, true);
      if (onLoadMore) {
        onLoadMore();
      }
    }
  }, [loading, hasMore, page, debouncedSearchTerm, fetchProducts, onLoadMore, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    products,
    loading,
    hasMore,
    loadMore,
    totalItems,
  };
};
